/**
 * Rollen zuordnen — am Asset, viele auf einmal.
 *
 * ## Warum es das gibt
 *
 * Im Termin mit der HOWOGE am 07.10.2026 (Punkt 16): wer eine neue
 * Applikation einführt, ordnet sie zwanzig oder dreißig Rollen zu. Bisher ging
 * das nur von der Rolle aus — zwanzig- oder dreißigmal der Rolleneditor.
 * Jetzt steht die Auswahl im Formular des Assets, beim Anlegen wie beim
 * Bearbeiten.
 *
 * ## Wie die Rollen gegliedert sind
 *
 * Nach der Kennung, wie im Sichtbereich der Konten (`Konten.tsx`): der erste
 * Teil ist der Bereich (`APM`), die ersten beiden Teile einer dreiteiligen
 * Kennung die Abteilung (`APM-AM`). Die Namen kommen aus den
 * Organisationseinheiten, wo ein Code passt. Alles ist zugeklappt, bis jemand
 * es öffnet — 321 Rollen offen untereinander wären eine Liste, keine Wahl.
 *
 * Ein Häkchen am Bereich oder an der Abteilung wählt alle Rollen darin. Es
 * hat drei Zustände: alle, keine, einige.
 *
 * ## Was hier nicht geschieht
 *
 * Geschrieben wird nichts. Der Baustein sammelt den **Plan** — welche Rollen
 * dazukommen, welche Zuordnungen archiviert werden —, und der Drawer reicht
 * ihn beim Speichern an `Anlegeweg.tsx` weiter. Dort läuft er als ein
 * Vorgang über `POST /api/zuordnungen/_sammeln`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { stil } from './stil';
import { useAlleSeiten, useIch, useListe } from '../api/haken';
import { ARTEN_ALLE, ARTEN_JE_OBJEKTART } from '../entwurf/formulare';
import { hatRecht } from '../entwurf/navigation';
import { GRUND_MINDESTENS } from '../api/stilllegen';
import { SAMMEL_HOECHSTENS } from '../api/sammelzuordnung';

/* ------------------------------------------------------------ Plan */

/** Was beim Speichern an Zuordnungen geschehen soll. */
export interface Rollenplan {
  /** Rollen, die neu zugeordnet werden. */
  hinzu: string[];
  /** Die Berechtigungsart für alle neuen Zuordnungen. */
  art: string;
  /** Zuordnungen, die archiviert werden: Kennung → Zeilenversion beim Abwählen. */
  weg: Record<string, string>;
  /** Pflicht, sobald etwas archiviert wird. */
  grund: string;
}

export const LEERER_PLAN: Rollenplan = { hinzu: [], art: '', weg: {}, grund: '' };

/** Steht im Plan überhaupt etwas? */
export function planBelegt(plan: Rollenplan | null | undefined): boolean {
  return !!plan && (plan.hinzu.length > 0 || Object.keys(plan.weg).length > 0);
}

/** Was am Plan noch fehlt — `null`, wenn er sich abschicken lässt. */
export function planFehler(plan: Rollenplan | null | undefined): string | null {
  if (!plan) return null;
  const weg = Object.keys(plan.weg).length;
  if (plan.hinzu.length + weg > SAMMEL_HOECHSTENS) {
    return `Höchstens ${SAMMEL_HOECHSTENS} Änderungen auf einmal — bitte in Schritten speichern.`;
  }
  if (weg > 0 && plan.grund.trim().length < GRUND_MINDESTENS) {
    return `Für das Entfernen braucht es eine Begründung (mindestens ${GRUND_MINDESTENS} Zeichen).`;
  }
  return null;
}

/** Die passenden Berechtigungsarten einer Objektart — die erste ist die Vorgabe. */
export function artenFuerTyp(assetTyp: string): string[] {
  return ARTEN_JE_OBJEKTART[assetTyp] ?? ARTEN_ALLE;
}

/* ------------------------------------------------------------ Daten */

interface RolleKurz { rolleId: string; name: string }
interface Einheit { code: string; name: string }
interface Vorhanden {
  berechtigungId: string; rolleId: string; asset: string; assetTyp: string;
  kategorie?: string | null; berechtigungsart: string; __version?: string | null;
}

interface Ast {
  schluessel: string;
  einheit: string | null;
  /** Rollen direkt an diesem Ast — beim Bereich die ohne Abteilung. */
  rollen: RolleKurz[];
  unter: Ast[];
}

/** Bereich und Abteilung aus der Kennung — dieselbe Regel wie im Sichtbereich. */
function gliedern(rollen: RolleKurz[], namen: Map<string, string>): Ast[] {
  const bereiche = new Map<string, Ast>();
  for (const r of rollen) {
    const teile = r.rolleId.split('-');
    const b = (teile[0] || r.rolleId).toUpperCase();
    let bereich = bereiche.get(b);
    if (!bereich) {
      bereich = { schluessel: b, einheit: namen.get(b) ?? null, rollen: [], unter: [] };
      bereiche.set(b, bereich);
    }
    /* Die Abteilung nur bei dreiteiligen Kennungen: bei `IT-01` wäre `IT-01`
       die Rolle selbst. */
    if (teile.length >= 3 && teile[0] && teile[1]) {
      const a = (teile[0] + '-' + teile[1]).toUpperCase();
      let abteilung = bereich.unter.find((u) => u.schluessel === a);
      if (!abteilung) {
        abteilung = { schluessel: a, einheit: namen.get(a) ?? null, rollen: [], unter: [] };
        bereich.unter.push(abteilung);
      }
      abteilung.rollen.push(r);
    } else {
      bereich.rollen.push(r);
    }
  }
  const ordnen = (a: Ast, b: Ast) => a.schluessel.localeCompare(b.schluessel, 'de');
  const liste = [...bereiche.values()].sort(ordnen);
  for (const b of liste) b.unter.sort(ordnen);
  return liste;
}

const alleRollen = (a: Ast): RolleKurz[] => [...a.rollen, ...a.unter.flatMap(alleRollen)];

/* ------------------------------------------------------------ Baustein */

export interface RollenzuordnungProps {
  mandant: string;
  /**
   * Das bestehende Asset — Name, Objektart und Kategorie, unter denen seine
   * Zuordnungen stehen. `null` beim Anlegen: dann gibt es noch keine.
   *
   * Die Kategorie gehört dazu: AVD- und Surface-Fassung einer Anwendung sind
   * zwei Applikationen gleichen Namens (HOWOGE, Punkt 6).
   */
  bestand: { name: string; assetTyp: string; kategorie: string } | null;
  /** Die Objektart, für die neue Zuordnungen entstehen. */
  assetTyp: string;
  plan: Rollenplan;
  onPlan: (plan: Rollenplan) => void;
  /** Solange gespeichert wird, nimmt die Auswahl nichts an. */
  gesperrt: boolean;
}

export function Rollenzuordnung({
  mandant, bestand, assetTyp, plan, onPlan, gesperrt,
}: RollenzuordnungProps) {
  const ich = useIch(mandant);
  const rollenAbruf = useAlleSeiten<RolleKurz>('rollen', mandant, {
    sortierung: 'rolleId', richtung: 'auf',
    /* Archivierte Rollen bekommen keine neuen Zuordnungen. Hängt an einer
       noch eine, steht sie trotzdem da — unten aus den vorhandenen Zeilen. */
    filter: { statusNicht: 'archiviert' },
  });
  const einheiten = useListe<Einheit>('organisationseinheiten', mandant, {
    seite: 1, groesse: 200, sortierung: 'code', richtung: 'auf',
  });
  const vorhandenAbruf = useAlleSeiten<Vorhanden>('zuordnungen', mandant, {
    sortierung: 'rolleId', richtung: 'auf',
    /* Eine leere Kategorie laesst sich nicht filtern (die Schnittstelle weist
       leere Filter ab) — dann wird unten nachgefiltert. */
    filter: bestand
      ? {
        asset: bestand.name, assetTyp: bestand.assetTyp,
        ...(bestand.kategorie ? { kategorie: bestand.kategorie } : {}),
      }
      : {},
    aus: !bestand || !bestand.name,
  });

  const [suche, setzeSuche] = useState('');
  const [offen, setzeOffen] = useState<Record<string, boolean>>({});

  /* Ohne `zuordnung.schreiben` nur lesend. Solange die Rechte unterwegs
     sind, ebenfalls — nie mehr, als einem zusteht. */
  const rechte = ich.daten?.rechte;
  const darfSchreiben = Array.isArray(rechte) && hatRecht(rechte, 'zuordnung.schreiben');
  const nurLesen = !darfSchreiben || gesperrt;

  const arten = artenFuerTyp(assetTyp);
  /* Passt die gewählte Art nicht mehr zur Objektart — etwa nach dem Wechsel
     von „Postfach" auf „Verteilerliste" —, rückt die Vorgabe nach. */
  useEffect(() => {
    if (!plan.art || !arten.includes(plan.art)) onPlan({ ...plan, art: arten[0] ?? '' });
  }, [assetTyp, plan, arten, onPlan]);

  /* Die vorhandenen Zeilen je Rolle. Nachgefiltert auf genau dieses Asset:
     der Filter der Schnittstelle trennt Werte an Kommas, und ein Name mit
     Komma träfe sonst zwei andere. Ebenso die Kategorie, wenn sie leer ist —
     sonst kämen die Zeilen des Zwillings mit Kategorie hinzu. */
  const vorhanden = useMemo(() => {
    const karte = new Map<string, Vorhanden[]>();
    if (!bestand) return karte;
    for (const z of vorhandenAbruf.daten?.zeilen ?? []) {
      if (z.asset !== bestand.name) continue;
      if ((z.kategorie ?? '') !== bestand.kategorie) continue;
      const liste = karte.get(z.rolleId) ?? [];
      liste.push(z);
      karte.set(z.rolleId, liste);
    }
    return karte;
  }, [vorhandenAbruf.daten, bestand]);

  const rollen = useMemo(() => {
    const liste = [...(rollenAbruf.daten?.zeilen ?? [])];
    const bekannt = new Set(liste.map((r) => r.rolleId));
    for (const id of vorhanden.keys()) {
      if (!bekannt.has(id)) liste.push({ rolleId: id, name: 'archivierte Rolle' });
    }
    return liste;
  }, [rollenAbruf.daten, vorhanden]);

  const q = suche.trim().toLowerCase();
  const baum = useMemo(() => {
    const namen = new Map<string, string>();
    for (const e of einheiten.daten?.zeilen ?? []) namen.set(e.code.toUpperCase(), e.name);
    const gefiltert = q
      ? rollen.filter((r) => r.rolleId.toLowerCase().includes(q) || r.name.toLowerCase().includes(q))
      : rollen;
    return gliedern(gefiltert, namen);
  }, [rollen, einheiten.daten, q]);

  /* --- Zustand einer Rolle ------------------------------------------- */

  const hinzu = useMemo(() => new Set(plan.hinzu), [plan.hinzu]);
  const gewaehlt = (rolleId: string): boolean => {
    const da = vorhanden.get(rolleId);
    if (da && da.length > 0) return !da.every((z) => z.berechtigungId in plan.weg);
    return hinzu.has(rolleId);
  };

  /** Setzt viele Rollen auf einmal — das Gruppenhäkchen und das einzelne. */
  const setzen = (ids: string[], an: boolean) => {
    if (nurLesen) return;
    const neuHinzu = new Set(plan.hinzu);
    const neuWeg = { ...plan.weg };
    for (const id of ids) {
      const da = vorhanden.get(id);
      if (da && da.length > 0) {
        for (const z of da) {
          if (an) delete neuWeg[z.berechtigungId];
          else neuWeg[z.berechtigungId] = String(z.__version ?? '');
        }
      } else if (an) {
        neuHinzu.add(id);
      } else {
        neuHinzu.delete(id);
      }
    }
    onPlan({ ...plan, hinzu: [...neuHinzu], weg: neuWeg });
  };

  /* --- Zahlen -------------------------------------------------------- */

  const zugeordnet = vorhanden.size;
  const neu = plan.hinzu.length;
  const wegZahl = Object.keys(plan.weg).length;
  const wegRollen = [...vorhanden.entries()]
    .filter(([, da]) => da.every((z) => z.berechtigungId in plan.weg)).length;
  /* Ohne Version lässt sich nichts archivieren — die Schnittstelle wiese es
     ab. Gesagt wird es hier, nicht erst nach dem Klick. */
  const ohneVersion = Object.values(plan.weg).filter((v) => !v).length;

  const suchtGerade = q !== '';
  const istOffen = (schluessel: string) => suchtGerade || !!offen[schluessel];
  const alleAeste = baum.flatMap((b) => [b.schluessel, ...b.unter.map((u) => u.schluessel)]);
  const allesOffen = alleAeste.length > 0 && alleAeste.every((s) => offen[s]);

  const laedt = rollenAbruf.laedt || (!!bestand && vorhandenAbruf.laedt && !vorhandenAbruf.daten);
  const fehler = rollenAbruf.fehler ?? vorhandenAbruf.fehler;

  /* --- Darstellung --------------------------------------------------- */

  const rolleZeile = (r: RolleKurz, tiefe: number) => {
    const da = vorhanden.get(r.rolleId) ?? [];
    const an = gewaehlt(r.rolleId);
    const istNeu = hinzu.has(r.rolleId);
    const wirdWeg = da.length > 0 && !an;
    return (
      <label key={r.rolleId} className="rz-zeile" style={stil(
        /* `flex-wrap`: bei 375 px rücken Berechtigungsart und Marke in eine
           zweite Zeile, statt das Fenster seitlich scrollen zu lassen. Die
           Einrückung bleibt knapp — zwei Ebenen zu je 26 px. */
        'display:flex;align-items:center;flex-wrap:wrap;column-gap:10px;row-gap:2px;'
        + 'min-height:44px;padding:6px 14px 6px '
        + (tiefe === 1 ? '40px' : '66px') + ';border-top:1px solid var(--border-subtle);'
        + (nurLesen ? 'cursor:default' : 'cursor:pointer'),
      )}>
        <input type="checkbox" checked={an} disabled={nurLesen}
               onChange={(e) => setzen([r.rolleId], e.target.checked)}
               style={stil('flex:0 0 auto;width:16px;height:16px;accent-color:var(--brand-primary)')} />
        <span translate="no" style={stil(
          'flex:0 0 auto;font-family:var(--mono);font-size:12.5px;font-weight:500',
        )}>{r.rolleId}</span>
        <span style={stil(
          'flex:1 1 120px;min-width:0;font-size:13px;color:var(--text-secondary);'
          + 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
        )}>{r.name}</span>
        {da.length > 0 && !wirdWeg && (
          <span style={stil(
            'flex:0 1 auto;min-width:0;max-width:100%;font-size:12px;color:var(--text-tertiary);'
            + 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
          )}>
            {da.map((z) => z.berechtigungsart).filter(Boolean).join(', ') || 'zugeordnet'}
          </span>
        )}
        {istNeu && <span className="marker marker--gut">neu</span>}
        {wirdWeg && <span className="marker marker--fehler">wird archiviert</span>}
      </label>
    );
  };

  const astKopf = (a: Ast, tiefe: number) => {
    const ids = alleRollen(a).map((r) => r.rolleId);
    const an = ids.filter(gewaehlt).length;
    const zustand: Dreizustand = an === 0 ? 'aus' : an === ids.length ? 'an' : 'teils';
    const auf = istOffen(a.schluessel);
    const titel = a.schluessel + (a.einheit ? ' · ' + a.einheit : '');
    return (
      <div style={stil(
        'display:flex;align-items:center;gap:4px;min-height:44px;padding:0 14px 0 '
        + (tiefe === 0 ? '6px' : '32px') + ';border-top:1px solid var(--border-subtle);'
        + (tiefe === 0 ? 'background:var(--surface-sunken)' : ''),
      )}>
        <Dreifach zustand={zustand} gesperrt={nurLesen || ids.length === 0}
                  label={(zustand === 'an' ? 'Alle Rollen abwählen: ' : 'Alle Rollen wählen: ') + titel}
                  onAendern={(neuAn) => setzen(ids, neuAn)} />
        <button type="button" onClick={() => setzeOffen({ ...offen, [a.schluessel]: !auf })}
                aria-expanded={auf}
                aria-label={titel + ', ' + an + ' von ' + ids.length + ' gewählt, '
                  + (auf ? 'zuklappen' : 'aufklappen')}
                disabled={suchtGerade}
                className="rz-ast"
                style={stil(
                  'flex:1;min-width:0;display:flex;align-items:center;gap:9px;min-height:44px;'
                  + 'padding:0 6px;background:none;border:none;border-radius:var(--r-md);'
                  + 'color:var(--text-primary);text-align:left;'
                  + (suchtGerade ? 'cursor:default' : 'cursor:pointer'),
                )}>
          <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="3" strokeLinecap="round"
               style={stil(
                 'flex:0 0 11px;color:var(--text-tertiary);'
                 + 'transition:transform var(--dauer-kurz) var(--kurve-aus);'
                 + (auf ? 'transform:rotate(90deg)' : ''),
               )}>
            <path d="M9 6l6 6-6 6" />
          </svg>
          <span translate="no" style={stil(
            'flex:0 0 auto;font-family:var(--mono);font-size:12.5px;'
            + (tiefe === 0 ? 'font-weight:600' : 'font-weight:500'),
          )}>{a.schluessel}</span>
          <span style={stil(
            'flex:1;min-width:0;font-size:13px;color:var(--text-secondary);'
            + 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
          )}>{a.einheit ?? ''}</span>
          <span className="num" style={stil('flex:0 0 auto;font-size:12px;color:var(--text-secondary)')}>
            {an} / {ids.length}
          </span>
        </button>
      </div>
    );
  };

  return (
    <section aria-labelledby="rz-titel" className="rz-schale" style={stil('margin-bottom:20px')}>
      <div style={stil(
        'display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;margin-bottom:10px',
      )}>
        <h3 id="rz-titel" style={stil(
          'margin:0;font-size:12px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;'
          + 'color:var(--text-tertiary)',
        )}>Rollen zuordnen</h3>
        {/* Die Zahlen sprechen mit: wer abwählt, hört, was sich ändert. */}
        <span role="status" aria-live="polite" className="num" style={stil(
          'font-size:12.5px;color:var(--text-secondary)',
        )}>
          {bestand ? `${zugeordnet} zugeordnet` : 'noch keine Zuordnung'}
          {neu > 0 && ` · ${neu} neu`}
          {wegRollen > 0 && ` · ${wegRollen} ${wegRollen === 1 ? 'wird' : 'werden'} archiviert`}
        </span>
      </div>

      {!darfSchreiben && !ich.laedt && ich.daten && (
        <p style={stil('margin:0 0 10px;font-size:12.5px;color:var(--text-secondary)')}>
          Nur lesend — zum Zuordnen fehlt das Recht „zuordnung.schreiben“.
        </p>
      )}

      {/* Eine Leiste, eine Grundlinie (07.10.2026): Suche und Zugriffsart
          tragen beide eine Beschriftung darüber, alle drei Bedienelemente
          haben dieselbe Höhe. Vorher stand nur über der Auswahl ein Text, und
          Suchfeld, Auswahl und Knopf saßen auf drei verschiedenen Höhen.
          Raster und Umbruch stehen in `bausteine.css` (`.rz-leiste`). */}
      <div className="rz-leiste">
        <label className="rz-feld">
          <span>Rolle suchen</span>
          <input value={suche} onChange={(e) => setzeSuche(e.target.value)}
                 className="formularfeld" type="search"
                 placeholder="Kennung oder Name"
                 autoComplete="off" spellCheck={false} />
        </label>
        <label className="rz-feld rz-feld--art">
          <span>Zugriffsart für neue Zuordnungen</span>
          <select value={plan.art} disabled={nurLesen}
                  onChange={(e) => onPlan({ ...plan, art: e.target.value })}
                  className="formularfeld">
            {arten.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <button type="button" className="knopf knopf--rand" aria-expanded={allesOffen}
                disabled={suchtGerade || baum.length === 0}
                onClick={() => setzeOffen(allesOffen
                  ? {} : Object.fromEntries(alleAeste.map((s) => [s, true])))}>
          {allesOffen ? 'Alle zuklappen' : 'Alle aufklappen'}
        </button>
      </div>

      <div style={stil(
        'border:1px solid var(--border-subtle);border-radius:var(--r-md);overflow:hidden;'
        + 'background:var(--surface-card)',
      )}>
        {laedt && rollen.length === 0 && (
          <p style={stil('margin:0;padding:14px;font-size:13px;color:var(--text-secondary)')}>
            Rollen werden geladen…
          </p>
        )}
        {fehler && (
          <p role="alert" style={stil('margin:0;padding:14px;font-size:13px;color:var(--danger)')}>
            Die Rollen ließen sich nicht laden: {fehler}
          </p>
        )}
        {!laedt && !fehler && baum.length === 0 && (
          <p style={stil('margin:0;padding:14px;font-size:13px;color:var(--text-secondary)')}>
            {q ? `Keine Rolle passt zu „${suche.trim()}“.` : 'Es gibt noch keine Rolle.'}
          </p>
        )}
        {/* Die erste Trennlinie gehört dem Rahmen, nicht der ersten Zeile. */}
        <div style={stil('margin-top:-1px')}>
          {baum.map((b) => (
            <div key={b.schluessel}>
              {astKopf(b, 0)}
              {istOffen(b.schluessel) && (
                <>
                  {b.rollen.map((r) => rolleZeile(r, 1))}
                  {b.unter.map((u) => (
                    <div key={u.schluessel}>
                      {astKopf(u, 1)}
                      {istOffen(u.schluessel) && u.rollen.map((r) => rolleZeile(r, 2))}
                    </div>
                  ))}
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {wegZahl > 0 && (
        <div style={stil('margin-top:12px')}>
          <label htmlFor="rz-grund" style={stil(
            'display:block;font-size:13px;line-height:18px;font-weight:500;margin-bottom:4px',
          )}>
            Begründung für das Entfernen
            <span style={stil('color:var(--danger)')}> *</span>
          </label>
          <input id="rz-grund" value={plan.grund} disabled={nurLesen}
                 onChange={(e) => onPlan({ ...plan, grund: e.target.value })}
                 className="formularfeld"
                 aria-invalid={plan.grund.trim().length < GRUND_MINDESTENS}
                 aria-describedby="rz-grund-hinweis"
                 placeholder="Warum entfallen diese Zuordnungen?" />
          <div id="rz-grund-hinweis" style={stil('margin-top:4px;font-size:12px;color:var(--text-tertiary)')}>
            Mindestens {GRUND_MINDESTENS} Zeichen. Die Zuordnungen werden archiviert, nicht gelöscht —
            sie bleiben als Nachweis erhalten.
            {ohneVersion > 0 && ` ${ohneVersion} davon ohne Zeilenversion — bitte das Formular neu öffnen.`}
          </div>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------ Häkchen */

type Dreizustand = 'an' | 'aus' | 'teils';

/**
 * Ein Häkchen mit drei Zuständen.
 *
 * Den dritten — „einige" — kennt HTML nur als Eigenschaft, nicht als
 * Attribut: `indeterminate` lässt sich nicht ins Markup schreiben und wird
 * deshalb nach jedem Zeichnen am Element gesetzt. Vorleseprogramme melden
 * ihn dann als „teilweise ausgewählt".
 */
function Dreifach({ zustand, onAendern, label, gesperrt }: {
  zustand: Dreizustand;
  onAendern: (an: boolean) => void;
  label: string;
  gesperrt: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = zustand === 'teils';
  }, [zustand]);
  return (
    <label className="auswahlfeld" style={stil('flex:0 0 auto;min-width:36px;min-height:44px')}>
      <input ref={ref} type="checkbox" checked={zustand === 'an'} disabled={gesperrt}
             aria-label={label}
             onChange={() => onAendern(zustand !== 'an')}
             style={stil('width:16px;height:16px;accent-color:var(--brand-primary)')} />
    </label>
  );
}
